import React from "react";
import {
  defaultDropAnimationSideEffects,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  SnackbarContent,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useAuth } from "../../sdk";
import useChannelManager from "../hooks/useChannelManager";
import ChannelManagerResavenue from "./ChannelManagerResavenue";
import { ExculdeRooms } from "./ExcludeRooms";
import { SearchPropertyIdDialog } from "./SearchPropertyIdDialog";

const roomsthresoldList = [
  { key: "maxThresholdWeekday", label: "Max Threshold Weekday" },
  { key: "minThresholdWeekday", label: "Min Threshold Weekday" },
  { key: "maxThresholdWeekend", label: "Max Threshold Weekend" },
  { key: "minThresholdWeekend", label: "Min Threshold Weekend" },
];

const basePriceSeasons = [
  { key: "addToBasePriceHigh", label: "Add to base price - High Season" },
  { key: "addToBasePriceLow", label: "Add to base price - Low Season" },
];

const STAAH_CHANNEL_MANAGER_ID = 1;

export default function ChannelManager() {
  const {
    hotelId,
    handleRedirect,
    propertyId,
    channelManagerData,
    currentHotel,
    roomData,
    snackbarMsg,
    setSnackBarMsg,
    isLoading,
    handleClickConnectionButton,
    handlePushDataClick,
    arrData,
    openPropertyIdModal,
    setOpenPropertyIdModal,
    handleSearchPropertyId,
    openWarningModal,
    setOpenWarningModal,
    actionOnConnectionButtonClick,
    roomsData,
    setRoomsData,
    changeHandler,
    handleSaveRooms,
    loaderRoomIndex,
    excludeRoomToggle,
    setExcludeRoomToggle,
    authFetch,
    checkBoxFields,
    handleCheckBoxChange,
    handleSaveCheckBox,
    currentHotelData,
    reorderRoomMappingApi,
    isCheckBoxEdited,
    demandLevels,
  } = useChannelManager();
  const [active, setActive] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const data = useMemo(
    () => roomsData?.find((item) => item.sequence === active?.id),
    [active, roomsData]
  );

  const isSaveForRoomDisabled = useCallback(
    (index) => {
      const room = roomsData?.[index];
      if (!room || !room.roomId || !room.roomRateGroupCode || !room.isEdit) {
        return true;
      }
      if (room.isBaseRoom) {
        return false;
      }
      return !(room.addToBasePriceHigh && room.addToBasePriceLow);
    },
    [roomsData]
  );

  const { permission } = useAuth();

  const hasEnablePropertyConnectionPermission = useMemo(() => {
    if (!Array.isArray(permission)) return false;
    return permission.some((item) => item?.name === "enablePropertyConnection");
  }, [permission]);

  const isChannelManagerTypeStaah = useMemo(() => {
    return currentHotel?.channelManagerID === STAAH_CHANNEL_MANAGER_ID;
  }, [currentHotel]);

  if (excludeRoomToggle) {
    return (
      <ExculdeRooms
        excludeRoomToggle={excludeRoomToggle}
        setExcludeRoomToggle={setExcludeRoomToggle}
        authFetch={authFetch}
        hotelId={hotelId}
        propertyId={propertyId}
        roomData={roomData}
        handleSearchPropertyId={handleSearchPropertyId}
      />
    );
  }

  return (
    <ChannelManagerPageStack>
      <Stack className="toggleContainer">
        <Typography className="pageHeading">Channel Manager</Typography>
        <Button variant="contained" onClick={() => setExcludeRoomToggle(true)}>
          Exclude Rooms
        </Button>
      </Stack>
      <Stack className="borderStack" flexDirection="row">
        <Stack gap="16px" flexDirection="row">
          <Stack className="propertyStack">
            <TextField
              sx={{ width: "60%" }}
              label="Property Id"
              type="number"
              className="remove-arrows"
              value={propertyId}
              disabled={!!propertyId}
            />
            <Button
              variant="contained"
              sx={{ height: "40px" }}
              onClick={() => {
                handleSearchPropertyId();
              }}
              disabled={!propertyId}
            >
              Search
            </Button>
          </Stack>

          <Stack className="propertyStack">
            {checkBoxFields.map((field) => (
              <FormControlLabel
                key={field.key}
                value={currentHotelData?.[field.key] ?? false}
                sx={{
                  marginLeft: "0px",
                }}
                control={
                  <Checkbox
                    className="setup-checkbox"
                    checked={currentHotelData?.[field.key] ?? false}
                  />
                }
                onChange={(e) => {
                  handleCheckBoxChange(field.key);
                }}
                label={field.label}
              />
            ))}
            <TextField
              label="No. of Days Auto Push"
              type="number"
              className="remove-arrows"
              value={currentHotelData?.noOfDaysChannelManagerForPush}
              InputLabelProps={{
                shrink: currentHotelData?.noOfDaysChannelManagerForPush != null,
              }}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                handleCheckBoxChange("noOfDaysChannelManagerForPush", value);
              }}
            />
            <FormControl sx={{ m: 1, width: 200 }}>
              <InputLabel id="high-season-multiple-checkbox-label">
                High Season
              </InputLabel>
              <Select
                labelId="high-season-multiple-checkbox-label"
                id="high-season-multiple-checkbox"
                multiple
                value={currentHotelData?.highDemandLevels ?? []}
                onChange={(event) =>
                  handleCheckBoxChange("highDemandLevels", event.target.value)
                }
                input={<OutlinedInput label="High Season" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {demandLevels.map((item) => (
                  <MenuItem key={item?.id} value={item?.symbol}>
                    <Checkbox
                      checked={currentHotelData?.highDemandLevels?.includes(
                        item?.symbol
                      )}
                    />
                    <ListItemText primary={item?.symbol} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
        <LoadingButton
          variant="contained"
          type="submit"
          loading={isLoading}
          sx={{ height: "40px" }}
          disabled={!isCheckBoxEdited}
          onClick={() => handleSaveCheckBox()}
        >
          Save
        </LoadingButton>
      </Stack>

      {currentHotel?.channelManagerID && (
        <ChannelManagerResavenue
          isResavenue={currentHotel?.channelManagerID === 2}
        />
      )}

      {/* Code Needs to be removed */}
      {/*       
      <Typography className="pageHeading">
        Room Mapping For Channel Manager
      </Typography>

      <DndContext
        sensors={sensors}
        onDragStart={({ active }) => {
          setActive(active);
        }}
        onDragEnd={({ active, over }) => {
          if (over && active.id !== over?.id) {
            const activeIndex = roomsData.findIndex(
              ({ sequence }) => sequence === active.id
            );
            const overIndex = roomsData.findIndex(
              ({ sequence }) => sequence === over.id
            );
            if (activeIndex !== -1 && overIndex !== -1) {
              const activeId = roomsData[activeIndex].RoomRatePlanId;
              setRoomsData(arrayMove(roomsData, activeIndex, overIndex));
              reorderRoomMappingApi(activeId, activeIndex + 1, overIndex + 1);
            }
          }

          setActive(null);
        }}
        onDragCancel={() => {
          setActive(null);
        }}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={roomsData}
          strategy={verticalListSortingStrategy}
        >
          {roomsData?.map((room, index) => (
            <SortableItem
              key={`${room?.RoomRatePlanId} - ${index}`}
              id={room?.sequence}
            >
              <RoomItem
                room={room}
                index={index}
                changeHandler={changeHandler}
                handleSaveRooms={handleSaveRooms}
                isSaveForRoomDisabled={isSaveForRoomDisabled}
                isLoading={isLoading}
                loaderRoomIndex={loaderRoomIndex}
              />
            </SortableItem>
          ))}
        </SortableContext>

        {data ? (
          <SortableOverlay>
            <RoomItem
              room={data}
              index={data.sequence - 1}
              changeHandler={changeHandler}
              handleSaveRooms={handleSaveRooms}
              isSaveForRoomDisabled={isSaveForRoomDisabled}
              isLoading={isLoading}
              loaderRoomIndex={loaderRoomIndex}
              isOverlay
            />
          </SortableOverlay>
        ) : null}
      </DndContext>

      <Stack
        className=""
        style={{
          marginLeft: "auto",
        }}
      >
        <Button
          variant="contained"
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={() => {
            // Add New Room to Array
            setRoomsData((prev) =>
              prev.concat({
                isNewRoom: true,
              })
            );
          }}
        >
          Add Room
        </Button>
      </Stack>
      <Stack className="buttonStack">
        <Stack direction="row" gap="16px" justifyContent="end">
          <Button
            variant="contained"
            onClick={() =>
              handleRedirect(`/hotel/${hotelId}/setup/competitors`)
            }
          >
            Next
          </Button>
        </Stack> */}

      {isChannelManagerTypeStaah && hasEnablePropertyConnectionPermission && (
        <Stack direction="row" gap="16px" justifyContent="space-between">
          <Button
            variant="contained"
            // disabled={currentHotel?.enablePushDataAPI}
            onClick={handlePushDataClick}
          >
            {currentHotel?.enablePushData
              ? "Disable Push Data"
              : "Enable Push Data"}
          </Button>
          <Button
            variant="contained"
            onClick={actionOnConnectionButtonClick}
            // disabled={currentHotel?.enablePropertyConnection}
          >
            {currentHotel?.enablePropertyConnection
              ? "Disable Connection"
              : "Enable Connection"}
          </Button>
        </Stack>
      )}

      {/* </Stack> */}

      {/* {arrData && ( 
        <Stack>
          <pre>{JSON.stringify(arrData, null, 10)}</pre>
        </Stack>
      )} */}
      {openPropertyIdModal ? (
        <SearchPropertyIdDialog
          isLoading={isLoading}
          openPropertyIdModal={openPropertyIdModal}
          roomData={roomData}
          setOpenPropertyIdModal={setOpenPropertyIdModal}
        />
      ) : null}

      <StyledDialog
        open={openWarningModal}
        onClose={() => setOpenWarningModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="content">
          <Stack direction="row" alignItems="center" gap="8px">
            <Typography fontWeight="600" fontSize="24px">
              Warning
            </Typography>
            <WarningIcon sx={{ color: "#a90f0f" }} />
          </Stack>
        </DialogTitle>

        <CloseIcon
          className="closeButton"
          onClick={() => setOpenWarningModal(false)}
        />
        <DialogContent className="content">
          <Typography textAlign="center">
            Are you sure you want to disable the connection , as this might
            create problem in fetching and pushing the ARR to the channel
            Manager
          </Typography>
          <Stack direction="row" gap="40px" padding="24px">
            <Button
              variant="contained"
              onClick={() => setOpenWarningModal(false)}
            >
              No
            </Button>
            <Button variant="contained" onClick={handleClickConnectionButton}>
              Yes
            </Button>
          </Stack>
        </DialogContent>
      </StyledDialog>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={snackbarMsg.open}
        autoHideDuration={6000}
        onClose={() => {
          setSnackBarMsg({
            ...snackbarMsg,
            open: false,
          });
        }}
      >
        <SnackbarContent
          sx={{
            backgroundColor: snackbarMsg.color,
            textTransform: "capitalize",
          }}
          message={snackbarMsg.msg}
        />
      </Snackbar>
    </ChannelManagerPageStack>
  );
}

const RoomItem = ({
  room,
  index,
  changeHandler,
  handleSaveRooms,
  isSaveForRoomDisabled,
  isLoading,
  loaderRoomIndex,
  isOverlay = false,
}) => {
  const { currentHotel } = useAuth();
  const isChannelManagerTypeResavenue = useMemo(
    () => currentHotel?.channelManagerID === 2,
    [currentHotel?.channelManagerID]
  );

  const fields = [
    {
      label: "Room Id",
      type: "number",
      key: "roomId",
      show: true,
    },
    {
      label: "Room Name",
      type: "text",
      key: "roomName",
      show: true,
    },
    {
      label: "Rate Id",
      type: "number",
      key: "roomRateGroupCode",
      show: true,
    },
    {
      label: "Room Rate Plan Name",
      type: "text",
      key: "roomRatePlanName",
      show: isChannelManagerTypeResavenue,
    },
    {
      label: "Promotional Discount",
      type: "number",
      key: "promotionalDiscount",
      show: true,
    },
    {
      label: "Add to Double Rate",
      type: "number",
      key: "addToDoubleOccupancy",
      show: isChannelManagerTypeResavenue,
    },
  ];

  return (
    <Stack className="borderStack" gap="16px">
      <Stack gap="16px" flexDirection={"row"} width={"100%"}>
        <Stack
          display={"flex"}
          flexGrow={"1"}
          flexDirection={"column"}
          gap={"16px"}
        >
          <Stack direction="row" gap="16px">
            <TextField
              label="Sequence"
              type="number"
              className="remove-arrows"
              sx={{ width: "6%" }}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              value={isOverlay ? room.sequence : index + 1}
            />
            {fields
              .filter((item) => item.show)
              .map((field) => (
                <TextField
                  key={field.key}
                  label={field.label}
                  type={field.type}
                  className={field.type === "number" ? "remove-arrows" : ""}
                  value={room?.[field.key]}
                  {...(!isOverlay && {
                    onMouseDown: (e) => e.stopPropagation(),
                    onKeyDown: (e) => e.stopPropagation(),
                    onChange: (e) =>
                      changeHandler(index, field.key, e.target.value),
                  })}
                />
              ))}
          </Stack>
          <Stack direction="row" gap="16px">
            {isChannelManagerTypeResavenue && (
              <TextField
                label="Add to Triple Rate"
                type="number"
                className="remove-arrows"
                value={room?.addToTripleOccupancy}
                {...(!isOverlay && {
                  onMouseDown: (e) => e.stopPropagation(),
                  onKeyDown: (e) => e.stopPropagation(),
                  onChange: (e) =>
                    changeHandler(
                      index,
                      "addToTripleOccupancy",
                      e.target.value
                    ),
                })}
              />
            )}
            {isChannelManagerTypeResavenue && (
              <TextField
                label="Add to Quad Rate"
                type="number"
                className="remove-arrows"
                value={room?.addToQuadOccupancy}
                {...(!isOverlay && {
                  onMouseDown: (e) => e.stopPropagation(),
                  onKeyDown: (e) => e.stopPropagation(),
                  onChange: (e) =>
                    changeHandler(index, "addToQuadOccupancy", e.target.value),
                })}
              />
            )}

            {!room?.isBaseRoom &&
              basePriceSeasons.map((item) => (
                <TextField
                  required
                  key={item.key}
                  label={item.label}
                  type="number"
                  className="remove-arrows"
                  value={room?.[item.key]}
                  {...(!isOverlay && {
                    onMouseDown: (e) => e.stopPropagation(),
                    onKeyDown: (e) => e.stopPropagation(),
                    onChange: (e) =>
                      changeHandler(index, item.key, e.target.value),
                  })}
                />
              ))}

            {roomsthresoldList?.map((thresold) => (
              <TextField
                key={`thresold-column-${thresold.key}`}
                label={thresold.label}
                type="number"
                className="remove-arrows"
                value={room?.[thresold.key]}
                {...(!isOverlay && {
                  onMouseDown: (e) => e.stopPropagation(),
                  onKeyDown: (e) => e.stopPropagation(),
                  onChange: (e) =>
                    changeHandler(index, thresold.key, e.target.value),
                })}
              />
            ))}
          </Stack>
        </Stack>
        <Stack justifyContent="space-between" minWidth={"180px"}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                size="small"
                checked={room?.isBaseRoom}
                {...(!isOverlay && {
                  onMouseDown: (e) => e.stopPropagation(),
                  onKeyDown: (e) => e.stopPropagation(),
                  onChange: () =>
                    changeHandler(index, "isBaseRoom", !room?.isBaseRoom),
                })}
              />
            }
            label="Is Base Room"
          />
          {isChannelManagerTypeResavenue && (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  size="small"
                  checked={room?.isBaseRatePlan}
                  {...(!isOverlay && {
                    onMouseDown: (e) => e.stopPropagation(),
                    onKeyDown: (e) => e.stopPropagation(),
                    onChange: () =>
                      changeHandler(
                        index,
                        "isBaseRatePlan",
                        !room?.isBaseRatePlan
                      ),
                  })}
                />
              }
              label="Is Base Rate Plan"
            />
          )}
          <LoadingButton
            variant="contained"
            disabled={
              isOverlay
                ? isSaveForRoomDisabled(room.sequence)
                : isSaveForRoomDisabled(index)
            }
            loading={
              isLoading &&
              (isOverlay
                ? loaderRoomIndex === room.sequence
                : loaderRoomIndex === index)
            }
            {...(!isOverlay && {
              onClick: () =>
                handleSaveRooms(index, isChannelManagerTypeResavenue),
              onMouseDown: (e) => e.stopPropagation(),
              onKeyDown: (e) => e.stopPropagation(),
            })}
          >
            Save
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    opacity: isDragging ? 1 : null,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const dropAnimationConfig = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.4",
      },
    },
  }),
};

const SortableOverlay = ({ children }) => {
  return (
    <DragOverlay dropAnimation={dropAnimationConfig}>{children}</DragOverlay>
  );
};

const ChannelManagerPageStack = styled(Stack)(() => ({
  width: " 100%",
  display: "flex",
  border: "none",
  padding: "20px",
  outline: "none",
  height: "calc(100vh - 160px)",
  overflowY: "scroll",
  overflowX: "hidden",
  gap: "24px",

  ".borderStack": {
    backgroundColor: "#ffffff",
    gap: "16px",
    padding: "24px",
    boxShadow: "1px 4px 10px rgba(48, 111, 188, 0.2)",
    alignItems: "center",
    justifyContent: "space-between",
  },
  ".pageHeading": {
    fontSize: "16px",
    fontWeight: "600",
    paddingLeft: "16px",
  },
  ".toggleContainer": {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  ".propertyStack": {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    alignItems: "center",

    ".setup-checkbox": {
      color: "#163A90",
      "&.Mui-checked": {
        color: "#163A90",
      },
      "&.Mui-disabled": {
        color: "#AAAAAA",
      },
    },
  },
  ".buttonStack": {
    display: "flex",
    // flexDirection: "row",
    gap: "16px",
    justifyContent: "end",
  },
  ".remove-arrows": {
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      display: "none",
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    ".MuiInputAdornment-root": {
      "& fieldset": {
        border: "none",
      },
    },
  },
}));

const StyledDialog = styled(Dialog)(() => ({
  ".closeButton": {
    position: "absolute",
    right: "10px",
    top: "10px",
    cursor: "pointer",
  },
  ".content": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
}));
